// V1.0.64: Voice Recording services + types
import { apiClient } from './apiClient';

// ===== Types =====
export interface RecordingDevice {
  id: string;
  deviceCode: string;
  deviceName: string;
  location?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  machineId?: string | null;
  hostname?: string | null;
  macAddress?: string | null;
  isActive: boolean;
  isApproved: boolean;
  lastSeenAt?: string | null;
  lastChunkAt?: string | null;
  chunkDurationSec: number;
  autoTranscribe: boolean;
  notes?: string | null;
  registeredAt: string;
  approvedAt?: string | null;
}

export interface ApproveDeviceRequest {
  deviceCode: string;
  deviceName: string;
  location?: string;
  departmentId?: string;
  chunkDurationSec?: number;
  autoTranscribe?: boolean;
}

export interface VoiceRecord {
  id: string;
  deviceId: string;
  deviceCode: string;
  deviceName: string;
  fileName: string;
  fileSize: number;
  audioFormat: string;
  recordedAt: string;
  durationSec: number;
  sessionId?: string | null;
  sessionIdx?: number | null;
  windowsUser?: string | null;
  transcriptText?: string | null;
  transcriptStatus: 'PENDING' | 'PROCESSING' | 'DONE' | 'FAILED' | 'NOT_REQUESTED';
  transcriptEngine?: string | null;
  transcriptConfidence?: number | null;
  createdAt: string;
}

export interface VoiceSegment {
  id: string;
  segmentIndex: number;
  startSec: number;
  endSec: number;
  text: string;
  confidence?: number | null;
  recordedAtAbs: string;
}

export interface VoiceSearchHit {
  recordId: string;
  segmentId: string;
  deviceId: string;
  deviceCode: string;
  deviceName: string;
  departmentName?: string | null;
  recordedAt: string;
  absoluteTime: string;
  startSec: number;
  endSec: number;
  text: string;
  snippet: string;        // HTML có <mark>
  confidence?: number | null;
}

export interface VoiceSearchResult {
  total: number;
  page: number;
  pageSize: number;
  hits: VoiceSearchHit[];
}

export interface VoiceSession {
  sessionId: string;
  deviceId: string;
  deviceCode: string;
  deviceName: string;
  startedAt: string;
  endedAt: string;
  durationSec: number;
  chunkCount: number;
  chunks: VoiceRecord[];
}

export interface DeviceTimeline {
  deviceId: string;
  deviceCode: string;
  deviceName: string;
  date: string;
  totalDurationSec: number;
  totalChunks: number;
  sessions: VoiceSession[];
}

export interface DeviceStatus {
  deviceId: string;
  deviceCode: string;
  deviceName: string;
  location?: string | null;
  departmentName?: string | null;
  lastSeenAt?: string | null;
  lastChunkAt?: string | null;
  status: 'ACTIVE' | 'WARNING' | 'OFFLINE';
}

export interface DailyVolume {
  date: string;
  chunkCount: number;
  totalSizeBytes: number;
  totalDurationSec: number;
}

export interface VoiceDashboard {
  activeDevicesCount: number;
  totalDevicesCount: number;
  todayChunksCount: number;
  todayTotalSizeBytes: number;
  totalSizeBytes: number;
  activeDevices: DeviceStatus[];
  offlineDevices: DeviceStatus[];
  last7DaysVolume: DailyVolume[];
}

// ===== Service =====
export const voiceService = {
  // --- Devices ---
  async listDevices(params?: { approved?: boolean; departmentId?: string }): Promise<RecordingDevice[]> {
    const { data } = await apiClient.get('/recording-devices', { params });
    return data;
  },

  async getDevice(id: string): Promise<RecordingDevice> {
    const { data } = await apiClient.get(`/recording-devices/${id}`);
    return data;
  },

  async approveDevice(id: string, req: ApproveDeviceRequest): Promise<{ deviceId: string; deviceToken: string; message: string }> {
    const { data } = await apiClient.post(`/recording-devices/${id}/approve`, req);
    return data;
  },

  async updateDevice(id: string, req: Partial<RecordingDevice & { isActive: boolean }>): Promise<void> {
    await apiClient.patch(`/recording-devices/${id}`, req);
  },

  async deleteDevice(id: string): Promise<void> {
    await apiClient.delete(`/recording-devices/${id}`);
  },

  // --- Voice Records ---
  async listRecords(params: {
    deviceId?: string;
    departmentId?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ items: VoiceRecord[]; page: number; pageSize: number }> {
    const { data } = await apiClient.get('/voice-records', { params });
    return data;
  },

  async getRecord(id: string): Promise<VoiceRecord> {
    const { data } = await apiClient.get(`/voice-records/${id}`);
    return data;
  },

  async getSegments(recordId: string): Promise<VoiceSegment[]> {
    const { data } = await apiClient.get(`/voice-records/${recordId}/segments`);
    return data;
  },

  /** URL stream audio - dùng cho <audio src={} /> (KHÔNG dùng được vì thiếu JWT header) */
  getPlayUrl(recordId: string): string {
    return `${apiClient.defaults.baseURL}/voice-records/${recordId}/play`;
  },

  /**
   * Fetch audio file qua axios (có JWT header tự động).
   * Trả về blob URL (object URL) để gán vào <audio src>.
   * Caller phải gọi URL.revokeObjectURL() sau khi không dùng nữa.
   */
  async fetchAudioBlobUrl(recordId: string): Promise<string> {
    const { data } = await apiClient.get(`/voice-records/${recordId}/play`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(data as Blob);
  },

  /**
   * Tải audio file về máy (trigger browser download).
   */
  async downloadAudio(recordId: string, fileName?: string): Promise<void> {
    const { data, headers } = await apiClient.get(`/voice-records/${recordId}/play`, {
      responseType: 'blob',
    });
    const blob = data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Lấy filename từ Content-Disposition nếu có
    let suggestedName = fileName ?? `recording-${recordId}.opus`;
    const cd = headers['content-disposition'] as string | undefined;
    if (cd) {
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/.exec(cd);
      if (match?.[1]) suggestedName = decodeURIComponent(match[1]);
    }
    a.download = suggestedName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  async deleteRecord(id: string): Promise<void> {
    await apiClient.delete(`/voice-records/${id}`);
  },

  async getTimeline(deviceId: string, date?: string): Promise<DeviceTimeline> {
    const { data } = await apiClient.get(`/voice-records/timeline/device/${deviceId}`, {
      params: date ? { date } : {},
    });
    return data;
  },

  async getDashboard(): Promise<VoiceDashboard> {
    const { data } = await apiClient.get('/voice-records/dashboard');
    return data;
  },

  // --- Search ---
  async search(params: {
    q: string;
    deviceIds?: string[];
    departmentIds?: string[];
    from?: string;
    to?: string;
    hourFrom?: number;
    hourTo?: number;
    minConfidence?: number;
    page?: number;
    pageSize?: number;
  }): Promise<VoiceSearchResult> {
    const { data } = await apiClient.get('/voice-search', {
      params: {
        q: params.q,
        deviceIds: params.deviceIds?.join(','),
        departmentIds: params.departmentIds?.join(','),
        from: params.from,
        to: params.to,
        hourFrom: params.hourFrom,
        hourTo: params.hourTo,
        minConfidence: params.minConfidence,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    return data;
  },
};
